import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import PointsDisclaimer from '../common/PointsDisclaimer';
import FlairPreview from './FlairPreview';
import RaffleSection from './RaffleSection';
import './ShopPage.css';

// En varas visningsnamn: skins bär namnet på sitt prize, resten sitt eget.
const itemName = (item) => (item.prize ? item.prize.name : item.display_name) || '';

// Kategori = item_type för skins/custom, annars flair_kind. Det låter en
// spelare filtrera direkt på "Emblem" eller "Titlar" i stället för att först
// välja "Flair" och sedan gräva. Ordningen styr chip-raden.
const CATEGORIES = [
  'skin', 'custom', 'theme', 'emblem', 'title', 'name_color', 'title_plate', 'badge',
];

const categoryOf = (item) => (
  item.item_type === 'flair' ? item.flair_kind : item.item_type
);

/**
 * ShopPage — tipsbutiken: köp skins för intjänade tipspoäng.
 *
 * Publik fönstershopping (varulistan kräver ingen inloggning); köp kräver
 * inloggad spelare med aktiv access och tillräckligt saldo — plånboken
 * debiteras i Django-API:t under radlås, aldrig här. Efter ett köp
 * dispatchas ``wallet-refresh`` så saldo-chipen i headern uppdateras.
 *
 * Egen köphistorik visas under butiken med leveransstatus per köp och en
 * "Hämta leverans"-knapp för olevererade köp (t.ex. efter att trade URL
 * satts eller en trade-hold gått ut).
 *
 * @returns {JSX.Element} Butikssidan.
 */
export default function ShopPage() {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [balance, setBalance] = useState(null);
  const [housePot, setHousePot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmItem, setConfirmItem] = useState(null);
  const [buying, setBuying] = useState(false);
  const [message, setMessage] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  // Filtrering & sortering (helt klientsidan — listan är liten).
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('featured');
  const [search, setSearch] = useState('');
  const [affordableOnly, setAffordableOnly] = useState(false);

  const loadShop = useCallback(() => {
    fetch(`${API_URL}/api/shop/items`)
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((body) => setItems(body.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // Communitypotten (A4) — publikt saldo, visas i sidhuvudet.
    fetch(`${API_URL}/api/predictions/house-pot`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setHousePot(body); })
      .catch(() => {
        // tyst — potten är icke-kritisk för sidan
      });
  }, []);

  const loadPersonal = useCallback(() => {
    if (!user) return;
    fetch(`${API_URL}/api/predictions/wallet`, { headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setBalance(body.balance); })
      .catch(() => {
        // tyst — saldot är icke-kritiskt för sidan
      });
    fetch(`${API_URL}/api/shop/purchases`, { headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setPurchases(body.purchases || []); })
      .catch(() => {
        // tyst — historiken är icke-kritisk för sidan
      });
  }, [user, getAuthHeaders]);

  useEffect(() => { loadShop(); }, [loadShop]);
  useEffect(() => { loadPersonal(); }, [loadPersonal]);

  /**
   * Genomför köpet av vald vara och uppdatera saldo/lista/historik.
   */
  const buy = async () => {
    if (!confirmItem) return;
    setBuying(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/shop/purchase`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: confirmItem.id }),
      });
      const body = await res.json();
      if (!res.ok) {
        const key = {
          insufficient_balance: 'shop.errInsufficient',
          item_unavailable: 'shop.errUnavailable',
          already_owned: 'shop.errAlreadyOwned',
        }[body.code];
        setMessage({ kind: 'error', text: key ? t(key) : (body.error || t('shop.errGeneric')) });
      } else {
        setBalance(body.balance);
        const status = body.purchase?.trade_status;
        const type = body.purchase?.item_type;
        let textKey = 'shop.boughtPending';
        if (type === 'flair') textKey = 'shop.boughtFlair';
        else if (type === 'custom') textKey = 'shop.boughtCustom';
        else if (status === 'sent') textKey = 'shop.boughtSent';
        setMessage({ kind: 'success', text: t(textKey) });
        window.dispatchEvent(new CustomEvent('wallet-refresh'));
        loadShop();
        loadPersonal();
      }
    } catch {
      setMessage({ kind: 'error', text: t('shop.errGeneric') });
    } finally {
      setBuying(false);
      setConfirmItem(null);
    }
  };

  /**
   * Skicka (om) trade-offern för ett olevererat köp.
   *
   * @param {object} purchase Köp-rad från historiken.
   */
  const claim = async (purchase) => {
    setClaimingId(purchase.id);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/shop/purchases/${purchase.id}/claim`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const body = await res.json();
      if (!res.ok) {
        const text = body.code === 'no_trade_url' ? t('shop.errNoTradeUrl') : (body.error || t('shop.errGeneric'));
        setMessage({ kind: 'error', text });
      } else {
        setMessage({ kind: 'success', text: t('shop.claimSent') });
        loadPersonal();
      }
    } catch {
      setMessage({ kind: 'error', text: t('shop.errGeneric') });
    } finally {
      setClaimingId(null);
    }
  };

  const canAfford = (item) => balance !== null && balance >= item.price_points;
  const reclaimable = (p) => p.item_type === 'skin'
    && ['pending', 'failed', 'expired', 'declined'].includes(p.trade_status);

  /**
   * Var en flair syns när den bärs.
   *
   * Titeln och dess platta renderas bara i profilhuvudet — topplisteraden har
   * ingen plats för titlar. Namnstil, badge och emblem följer med överallt
   * namnet står. Skillnaden är inte uppenbar från kortet, och en köpt titel
   * som inte syns där man väntade sig känns som en trasig vara.
   *
   * @param {string} kind Varans `flair_kind`.
   * @returns {string} i18n-nyckelns suffix: `profile` eller `everywhere`.
   */
  const flairScope = (kind) => {
    if (kind === 'theme') return 'site';
    return kind === 'title' || kind === 'title_plate' ? 'profile' : 'everywhere';
  };

  /**
   * Förhandsvisning för en flair-vara på ett butikskort.
   *
   * Själva renderingen bor i den delade `FlairPreview` — butiken och profilens
   * inventory ska aldrig kunna visa samma vara olika. Här läggs bara kortets
   * egen ram runt.
   *
   * @param {object} item Flair-vara från butikslistan.
   * @returns {JSX.Element} Preview-element.
   */
  const flairPreview = (item) => (
    <div className="shop-card-image placeholder">
      <FlairPreview
        kind={item.flair_kind}
        value={item.flair_value}
        name={item.display_name}
        ownName={user?.steam_username || user?.username || 'DittNamn'}
      />
    </div>
  );

  // Bara kategorier som faktiskt har varor visas — annars döda chips i en
  // butik utan t.ex. emblem.
  const presentCategories = useMemo(() => {
    const present = new Set(items.map(categoryOf));
    return CATEGORIES.filter((c) => present.has(c));
  }, [items]);

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = items.filter((item) => {
      if (category !== 'all' && categoryOf(item) !== category) return false;
      if (q && !itemName(item).toLowerCase().includes(q)) return false;
      // "Har råd med" gömmer det man inte kan köpa — bara meningsfullt inloggad.
      if (affordableOnly && balance !== null && item.price_points > balance) return false;
      return true;
    });
    const byName = (a, b) => itemName(a).localeCompare(itemName(b), currentLocale());
    const sorters = {
      featured: (a, b) =>
        (a.sort_order - b.sort_order) || (a.price_points - b.price_points),
      price_asc: (a, b) => (a.price_points - b.price_points) || byName(a, b),
      price_desc: (a, b) => (b.price_points - a.price_points) || byName(a, b),
      name: byName,
    };
    // Kopia innan sort — sort muterar, och items är delat state.
    return [...list].sort(sorters[sort] || sorters.featured);
  }, [items, category, sort, search, affordableOnly, balance]);

  const statusLabel = (status) => {
    const key = {
      pending: 'shop.statusPending',
      sent: 'shop.statusSent',
      accepted: 'shop.statusAccepted',
      declined: 'shop.statusDeclined',
      expired: 'shop.statusExpired',
      failed: 'shop.statusFailed',
      manual: 'shop.statusManual',
    }[status];
    return key ? t(key) : status;
  };

  return (
    <div className="shop-page">
      <div className="shop-header">
        <h1 className="shop-title">🎁 {t('shop.title')}</h1>
        <p className="shop-subtitle">{t('shop.subtitle')}</p>
        {housePot && housePot.balance > 0 && (
          <div className="shop-balance shop-house-pot" title={t('housePot.tooltip', { pct: housePot.recycle_pct })}>
            🏦 {t('housePot.badge', { balance: housePot.balance.toLocaleString(currentLocale()) })}
          </div>
        )}
        {user && balance !== null && (
          <div className="shop-balance">
            🪙 {balance.toLocaleString(currentLocale())} {t('predictions.points')}
          </div>
        )}
      </div>

      <PointsDisclaimer />

      {message && (
        <div className={`shop-message ${message.kind}`}>{message.text}</div>
      )}

      <RaffleSection onBought={loadPersonal} />

      {!loading && items.length > 0 && (
        <div className="shop-filters">
          <div className="shop-filter-chips">
            <button
              type="button"
              className={`shop-chip ${category === 'all' ? 'active' : ''}`}
              onClick={() => setCategory('all')}
            >
              {t('shop.catAll')}
            </button>
            {presentCategories.map((c) => (
              <button
                key={c}
                type="button"
                className={`shop-chip ${category === c ? 'active' : ''}`}
                onClick={() => setCategory(c)}
              >
                {t(`shop.cat_${c}`)}
              </button>
            ))}
          </div>
          <div className="shop-filter-controls">
            <input
              type="search"
              className="shop-search"
              placeholder={t('shop.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="shop-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label={t('shop.sortLabel')}
            >
              <option value="featured">{t('shop.sortFeatured')}</option>
              <option value="price_asc">{t('shop.sortPriceAsc')}</option>
              <option value="price_desc">{t('shop.sortPriceDesc')}</option>
              <option value="name">{t('shop.sortName')}</option>
            </select>
            {user && balance !== null && (
              <label className="shop-affordable">
                <input
                  type="checkbox"
                  checked={affordableOnly}
                  onChange={(e) => setAffordableOnly(e.target.checked)}
                />
                {t('shop.affordableOnly')}
              </label>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="shop-loading">{t('shop.loading')}</div>
      ) : items.length === 0 ? (
        <div className="shop-empty">{t('shop.empty')}</div>
      ) : visibleItems.length === 0 ? (
        <div className="shop-empty">{t('shop.noMatches')}</div>
      ) : (
        <div className="shop-grid">
          {visibleItems.map((item) => (
            <div
              key={item.id}
              className={`shop-card type-${item.item_type}`}
              style={{ '--rarity-color': item.prize?.rarity_color || 'var(--border-color)' }}
            >
              {item.item_type === 'flair' ? (
                flairPreview(item)
              ) : item.image_url ? (
                <img
                  className="shop-card-image"
                  src={item.image_url}
                  alt={item.display_name}
                  loading="lazy"
                />
              ) : (
                <div className="shop-card-image placeholder">
                  {item.item_type === 'custom' ? '🎁' : '🔫'}
                </div>
              )}
              <div className="shop-card-body">
                <span className="shop-card-name">
                  {item.prize?.stattrak && <span className="shop-stattrak">StatTrak™ </span>}
                  {item.prize ? item.prize.name : item.display_name}
                </span>
                {item.prize && (
                  <span className="shop-card-condition">{item.prize.condition}</span>
                )}
                {item.description && (
                  <span className="shop-card-condition">{item.description}</span>
                )}
                {item.item_type === 'flair' && (
                  <span className="shop-card-scope">
                    {t(`shop.flairScope_${flairScope(item.flair_kind)}`)}
                  </span>
                )}
                {item.prize?.estimated_value_sek != null && (
                  <span className="shop-card-value">
                    ≈ {item.prize.estimated_value_sek.toLocaleString(currentLocale())} SEK
                  </span>
                )}
                {item.stock !== null && item.item_type !== 'skin' && (
                  <span className="shop-card-value">
                    {t('shop.stockLeft', { count: item.stock })}
                  </span>
                )}
              </div>
              <div className="shop-card-footer">
                <span className="shop-card-price">
                  🪙 {item.price_points.toLocaleString(currentLocale())}
                </span>
                {user ? (
                  <button
                    type="button"
                    className="shop-buy-btn"
                    disabled={!canAfford(item)}
                    onClick={() => setConfirmItem(item)}
                  >
                    {canAfford(item) ? t('shop.buy') : t('shop.cantAfford')}
                  </button>
                ) : (
                  <span className="shop-login-hint">{t('shop.loginToBuy')}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {user && purchases.length > 0 && (
        <div className="shop-history">
          <h2 className="shop-history-title">{t('shop.historyTitle')}</h2>
          <div className="shop-history-list">
            {purchases.map((p) => (
              <div key={p.id} className="shop-history-row">
                <span className="shop-history-name">
                  {p.prize?.stattrak && 'StatTrak™ '}{p.display_name}
                  {p.prize && (
                    <span className="shop-history-condition">{p.prize.condition}</span>
                  )}
                </span>
                <span className="shop-history-price">🪙 {p.price_paid}</span>
                <span className={`shop-history-status status-${p.trade_status}`}>
                  {statusLabel(p.trade_status)}
                </span>
                {reclaimable(p) && (
                  <button
                    type="button"
                    className="shop-claim-btn"
                    disabled={claimingId === p.id}
                    onClick={() => claim(p)}
                  >
                    {claimingId === p.id ? t('shop.claiming') : t('shop.claim')}
                  </button>
                )}
                <span className="shop-history-date">
                  {new Date(p.created_at).toLocaleDateString(currentLocale())}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {confirmItem && (
        <div className="shop-modal-backdrop" onClick={() => !buying && setConfirmItem(null)}>
          <div className="shop-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="shop-modal-title">{t('shop.confirmTitle')}</h3>
            <p className="shop-modal-text">
              {t('shop.confirmText', {
                name: confirmItem.display_name,
                price: confirmItem.price_points,
              })}
            </p>
            <div className="shop-modal-actions">
              <button
                type="button"
                className="shop-modal-cancel"
                disabled={buying}
                onClick={() => setConfirmItem(null)}
              >
                {t('shop.cancel')}
              </button>
              <button
                type="button"
                className="shop-modal-confirm"
                disabled={buying}
                onClick={buy}
              >
                {buying ? t('shop.buying') : t('shop.confirmBuy')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
